import { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material';
import {
  BalconyPlant,
  CellGrid,
  RoofElevation,
  StorageStack,
  StringDiagram,
  SunDial,
  Wallbox,
  YieldCurve,
} from './SolarShapes';
import './solar-background.css';

interface SolarBackgroundProps {
  /** Radius of the revealed area, in pixels. */
  revealRadius?: number;
}

/**
 * Fixed background layer of PV schematics — roof array, string wiring,
 * inverter, storage, wallbox, yield curve — hidden behind a mask that only
 * reveals what is near the cursor, plus a soft light that follows it.
 *
 * Purely decorative: `aria-hidden`, never interactive, and it disables itself
 * for reduced-motion users and on touch devices (no cursor to reveal with).
 */
export function SolarBackground({ revealRadius = 380 }: SolarBackgroundProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [isVisible, setVisible] = useState(false);
  const frame = useRef<number>();

  const onMove = useCallback((event: MouseEvent) => {
    if (frame.current) cancelAnimationFrame(frame.current);
    // Coalesce to one update per frame — mousemove fires far more often.
    frame.current = requestAnimationFrame(() => {
      setPosition({
        x: (event.clientX / window.innerWidth) * 100,
        y: (event.clientY / window.innerHeight) * 100,
      });
      setVisible(true);
    });
  }, []);

  const onLeave = useCallback(() => setVisible(false), []);

  useEffect(() => {
    window.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseleave', onLeave);
    return () => {
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [onMove, onLeave]);

  const vars = {
    '--mouse-x': `${position.x}%`,
    '--mouse-y': `${position.y}%`,
    '--reveal': `${revealRadius}px`,
    // Warm wash, like sun falling on glass — the one place orange appears
    // ambiently, kept far below the accent's saturation.
    '--glow-rgb': isDark ? '245, 166, 35' : '27, 75, 150',
  } as React.CSSProperties;

  return (
    <>
      <div className="solar-glow" style={{ ...vars, opacity: isVisible ? 1 : 0 }} aria-hidden />
      <div
        className="solar-bg"
        aria-hidden
        style={{
          ...vars,
          color: isDark ? 'rgba(163,190,219,0.34)' : 'rgba(14,42,92,0.26)',
        }}
      >
        <CellGrid />
        <div className="solar-bg__shapes">
          <RoofElevation />
          <StringDiagram />
          <YieldCurve />
          <StorageStack />
          <Wallbox />
          <BalconyPlant />
          <SunDial />
        </div>
      </div>
    </>
  );
}
